import {
  OKTAUserSeviceFlagEnum,
  OKTAUserTypeEnum,
} from "@app/store/useOKTAUserStore";
import { BrightcoveVideo } from "@components/BrightcoveVideo/BrightcoveVideo";
import ArticleCardCarousel, {
  Referer,
} from "@components/Carousel/ArticleCardCarousel";
import ArticleImage from "@components/Carousel/ArticleImage";
import CASBlock from "@components/CASBlock/CASBlock";
import AdsContainer from "@components/Container/AdsContainer";
import { LabelCardIconVariety } from "@components/Icon/LabelCardIcon";
import { AdsImu1, AdsMidArticle } from "@elements/Advertisement/variant/AdsImu";
import {
  CompanionAdsImu1,
  CompanionAdsMidArticle,
} from "@elements/Advertisement/variant/CompanionAdImu";
import DraggableCTA from "@elements/DraggableCTA/DraggableCTA";
import RelatedArticle from "@elements/RelatedArticle/RelatedArticle";
import Paragraph from "@elements/Typography/Paragraph";
import fetchBHApi from "@helper/fetchBHApi";
import {
  CAS_CDP_REGWALL_ID,
  MYSPH_NOT_AVAILABLE,
} from "@helper/getEnvVariables";
import hasServiceFlag from "@helper/hasServiceFlag";
import { getUserType } from "@helper/mySPH";
import { isArrayWithElements } from "@helper/utils";
import useCasScript from "@hooks/useCasScript";
import ArticleElementWrapper from "@pages/Article/Components/ArticleElementWrapper";
import { type ProcessedArticleData } from "@transformer/useOSResponse";
import { AdTargetingType, ArticleAdTargetKeys } from "@typings/Ads.d";
import {
  Attachment,
  type ProcessedArticleBody,
  ProcessedTagsContent,
  type RelatedArticles,
} from "@typings/Opensearch";
import React, { useCallback, useEffect, useState } from "react";

import AttachmentCard from "../AttachmentCard/AttachmentCard";
import RelatedArticleNew from "../RelatedArticle/RelatedArticleNew";
import { RenderTags } from "../Typography/Tags";
import ApplyParagraphAnnotations from "./ApplyParagraphAnnotations";
import { YOUTUBE_WATCH_URL } from "./Embeds/constants";
import Embed from "./Embeds/Embed";
import YouTubeEmbedCustom from "./Embeds/YouTubeEmbedCustom";
import GetLimitedParagraphsFromArticleBody from "./GetLimitedParagraphsFromArticleBody";
import ParagraphList from "./ParagraphElements/ParagraphList";
import PullQuoteCard from "./ParagraphElements/PullQuoteCard";
import SubHead from "./ParagraphElements/SubHead";
import WrapperContainer from "./ParagraphElements/WrapperContainer";
import RegistrationBanner from "./RegistrationBanner";
import SubscriptionBanner from "./SubscriptionBanner";

export type ArticleParagraphProps = {
  articleId: string;
  contentAccess: LabelCardIconVariety;
  articleBody: ProcessedArticleBody | null;
  uniqueSectionName: string | undefined;
  adsUniqueSectionName: string;
  relatedArticles: RelatedArticles;
  latestRegisteredArticle: ProcessedArticleData[];
  pageNumber: number;
  attachments?: Attachment[];
  tags?: ProcessedTagsContent[];
  isInfiniteScrollArticle?: boolean;
  gsChannels?: string[];
  articleSlotTarget?: AdTargetingType<ArticleAdTargetKeys, string>[];
  freeArticle?: ProcessedArticleData;
  visibleArticleId?: string;
  newDesign?: boolean;
};

export default function ArticleParagraph({
  freeArticle,
  articleId,
  contentAccess,
  articleBody,
  uniqueSectionName,
  adsUniqueSectionName,
  relatedArticles,
  latestRegisteredArticle,
  isInfiniteScrollArticle = false,
  pageNumber,
  gsChannels,
  articleSlotTarget,
  visibleArticleId,
  newDesign = false,
  attachments,
  tags,
}: ArticleParagraphProps) {
  const [showSubBanner, setShowSubBanner] = useState<boolean>(false);
  const [showGenGSubBanner, setShowGenGSubBanner] = useState<boolean>(false);
  const [showRegBanner, setShowRegBanner] = useState<boolean>(false);
  const [translatedArticle, setTranslatedArticle] = useState<
    ProcessedArticleData | undefined
  >(freeArticle);

  const [trimmedParagraphs, setTrimmedParagraphs] =
    useState<ProcessedArticleBody | null>(
      articleBody
        ? GetLimitedParagraphsFromArticleBody(articleBody, 2)
        : articleBody,
    );
  const [showNormalBanner, setShowNormalBanner] = useState<boolean>(false);
  const { isScriptReady: isCasScriptReady } = useCasScript();

  const fetchFullArticleBody = useCallback(() => {
    fetchBHApi<ProcessedArticleData[]>(`article/${articleId}`, "GET")
      .then((data) => {
        setTrimmedParagraphs(data[0].articleBody);
        setTranslatedArticle(data[0]);
      })
      .catch((err) => {
        console.error(err);
      });
  }, [articleId]);

  useEffect(() => {
    // Get the user type (e.g., y-sub, y-anoy, etc.)
    const userType = getUserType();

    if (parseInt(MYSPH_NOT_AVAILABLE)) {
      fetchFullArticleBody();
      // Condition 1: If the content requires a PREMIUM subscription,
      // the section is "gen-g", and the user doesn't have the "bh_geng" service flag,
      // show the Gen-G subscription banner and trim the article body to show only one paragraph.
    } else if (
      contentAccess === LabelCardIconVariety.PREMIUM &&
      uniqueSectionName === "gen-g" &&
      !hasServiceFlag(OKTAUserSeviceFlagEnum.BH_GENG)
    ) {
      setShowGenGSubBanner(true);
      setTrimmedParagraphs(
        articleBody && GetLimitedParagraphsFromArticleBody(articleBody, 1),
      );

      // Condition 2: If the content requires a PREMIUM subscription,
      // but the user is not a "y-sub" type, show the generic subscription banner.
    } else if (
      contentAccess === LabelCardIconVariety.PREMIUM &&
      uniqueSectionName !== "gen-g" &&
      (userType !== OKTAUserTypeEnum.SUBSCRIBER ||
        !hasServiceFlag(OKTAUserSeviceFlagEnum.BH_ONLINE))
    ) {
      setShowSubBanner(true);

      // Condition 3: If the content is for REGISTERED_USER,
      // and the user is either anonymous ("y-anoy") or the user type is undefined,
      // show the registration banner.
    } else if (
      contentAccess === LabelCardIconVariety.REGISTERED_USER &&
      userType === OKTAUserTypeEnum.ANONYMOUS
    ) {
      setShowRegBanner(true);

      // Condition 4: For gen g ala carte cases where the user is not a "y-anoy" type
      // and has service flag bh_geng, show the normal banner and set the
      // full article body to be displayed.
    } else if (
      uniqueSectionName === "gen-g" &&
      userType !== OKTAUserTypeEnum.ANONYMOUS &&
      hasServiceFlag(OKTAUserSeviceFlagEnum.BH_GENG)
    ) {
      fetchFullArticleBody();
      if (
        userType !== OKTAUserTypeEnum.SUBSCRIBER ||
        !hasServiceFlag(OKTAUserSeviceFlagEnum.BH_ONLINE)
      ) {
        setShowNormalBanner(true);
      }

      // Condition 5: For all other cases where the user is not a "y-sub" type,
      // show the normal banner and set the full article body to be displayed.
    } else if (
      userType !== OKTAUserTypeEnum.SUBSCRIBER ||
      !hasServiceFlag(OKTAUserSeviceFlagEnum.BH_ONLINE)
    ) {
      if (
        contentAccess === LabelCardIconVariety.REGISTERED_USER &&
        !isInfiniteScrollArticle
      ) {
        fetchFullArticleBody();
      } else {
        setTrimmedParagraphs(articleBody);
      }
      setShowNormalBanner(true);

      // Default: If none of the above conditions are met,
      // set the full article body to be displayed and hide all banners.
    } else {
      if (
        (contentAccess === LabelCardIconVariety.PREMIUM ||
          contentAccess === LabelCardIconVariety.REGISTERED_USER) &&
        !isInfiniteScrollArticle
      ) {
        fetchFullArticleBody();
      } else {
        setTrimmedParagraphs(articleBody);
      }
      setShowGenGSubBanner(false);
      setShowSubBanner(false);
      setShowRegBanner(false);
      setShowNormalBanner(false);
    }
  }, [
    contentAccess,
    articleBody,
    uniqueSectionName,
    fetchFullArticleBody,
    isInfiniteScrollArticle,
  ]);

  const casBlockProps = {
    key: `cas-${CAS_CDP_REGWALL_ID}`,
    casId: CAS_CDP_REGWALL_ID,
    ...(isInfiniteScrollArticle && { index: pageNumber }),
  };

  let paragraphCount = 0;
  let adsImu1 = (
    <AdsImu1
      uniqueSectionName={adsUniqueSectionName}
      isArticlePage={true}
      gsChannels={gsChannels}
      articleSlotTarget={articleSlotTarget}
    />
  );
  let adsMidArticle = (
    <AdsMidArticle
      uniqueSectionName={adsUniqueSectionName}
      isArticlePage={true}
      gsChannels={gsChannels}
      articleSlotTarget={articleSlotTarget}
    />
  );
  if (isInfiniteScrollArticle) {
    adsImu1 = (
      <CompanionAdsImu1
        uniqueSectionName={adsUniqueSectionName}
        index={pageNumber}
        gsChannels={gsChannels}
        articleSlotTarget={articleSlotTarget}
      />
    );
    adsMidArticle = (
      <CompanionAdsMidArticle
        uniqueSectionName={adsUniqueSectionName}
        index={pageNumber}
        gsChannels={gsChannels}
        articleSlotTarget={articleSlotTarget}
      />
    );
  }

  return (
    <div key={pageNumber} data-testid="article-body">
      {trimmedParagraphs &&
        trimmedParagraphs.map((element, index) => {
          let bannersAndAds = null;

          if (paragraphCount === 0 && showGenGSubBanner) {
            bannersAndAds = (
              <>
                <ArticleElementWrapper>
                  <SubscriptionBanner
                    isInfiniteScrollArticle={isInfiniteScrollArticle}
                    pageNumber={pageNumber}
                    isGenG={true}
                  />
                </ArticleElementWrapper>
                {!newDesign && adsImu1}
              </>
            );
          } else if (paragraphCount === 1) {
            bannersAndAds = (
              <>
                {showSubBanner && (
                  <ArticleElementWrapper>
                    <SubscriptionBanner
                      isInfiniteScrollArticle={isInfiniteScrollArticle}
                      pageNumber={pageNumber}
                    />
                  </ArticleElementWrapper>
                )}
                {showRegBanner && (
                  <ArticleElementWrapper>
                    <RegistrationBanner
                      isInfiniteScrollArticle={isInfiniteScrollArticle}
                      pageNumber={pageNumber}
                    />
                  </ArticleElementWrapper>
                )}
                {!newDesign && adsImu1}
              </>
            );
          } else if (newDesign && paragraphCount === 2) {
            bannersAndAds = (
              <AdsContainer className="mb-6 lg:mb-8">{adsImu1}</AdsContainer>
            );
          } else if (paragraphCount === 5) {
            bannersAndAds = newDesign ? (
              <AdsContainer className="mb-6 lg:mb-8">
                {adsMidArticle}
              </AdsContainer>
            ) : (
              <>{adsMidArticle}</>
            );
          } else if (
            !newDesign &&
            paragraphCount === 6 &&
            contentAccess === LabelCardIconVariety.FREE
          ) {
            bannersAndAds = (
              <div className="max-w-[825px] mb-8">
                <ArticleCardCarousel
                  renderedCount={3}
                  renderItems={latestRegisteredArticle}
                  className="mt-sm"
                  referer={Referer.ARTICLE}
                />
              </div>
            );
          }

          switch (element.type) {
            case "paragraph":
              paragraphCount++;
              return (
                <React.Fragment key={index}>
                  <ArticleElementWrapper>
                    <Paragraph
                      testid={`paragraph-${index}`}
                      className={
                        newDesign
                          ? "font-primary font-semimedium text-sm leading-[150%] text-black-800 mb-6 lg:mb-8"
                          : "font-extrabold text-sm lg:text-2md"
                      }
                    >
                      {ApplyParagraphAnnotations(
                        element.value,
                        element.annotations,
                      )}
                    </Paragraph>
                  </ArticleElementWrapper>
                  {bannersAndAds}
                </React.Fragment>
              );

            case "sub_head":
              return (
                <React.Fragment key={index}>
                  <ArticleElementWrapper>
                    <SubHead
                      element={element}
                      className={
                        newDesign
                          ? "font-secondary text-md text-black-800 leading-[150%] mb-6 lg:mb-8"
                          : ""
                      }
                    />
                  </ArticleElementWrapper>
                </React.Fragment>
              );

            case "image": {
              const imageHeight =
                element.image?.height && element.image.height >= 1460
                  ? 1460
                  : 550;
              return (
                <React.Fragment key={index}>
                  <ArticleElementWrapper>
                    <ArticleImage
                      index={index.toString()}
                      url={element.image?.url}
                      alt={element.caption}
                      caption={element.caption}
                      credit={element.credit}
                      height={imageHeight}
                      className={
                        newDesign ? "max-w-[860px] mb-6 lg:mb-8" : "my-12"
                      }
                    />
                  </ArticleElementWrapper>
                </React.Fragment>
              );
            }

            case "embed":
              return (
                <React.Fragment key={index}>
                  <ArticleElementWrapper>
                    <Embed
                      className={newDesign ? "my-6 lg:my-lg" : "my-lg"}
                      src={element.embedUrl}
                    />
                  </ArticleElementWrapper>
                </React.Fragment>
              );

            case "pull_quote":
              return (
                <React.Fragment key={index}>
                  <ArticleElementWrapper>
                    <PullQuoteCard
                      quote={element.quote}
                      quotee={element.quotee}
                      quoteLabel={element.quoteLabel}
                      quoteCredit={element.quoteCredit}
                      newDesign={newDesign}
                    />
                  </ArticleElementWrapper>
                </React.Fragment>
              );

            case "list_bulleted":
            case "list_numbered": {
              const listType = element.type === "list_bulleted" ? "ol" : "ul";
              const listItems = element.listItem;
              const listStyle =
                element.listStyle === "decimal" ? "list-decimal" : "list-disc";
              return (
                <React.Fragment key={index}>
                  <ArticleElementWrapper>
                    <ParagraphList
                      listStyle={listStyle}
                      listType={listType}
                      listItems={listItems}
                      newDesign={newDesign}
                    />
                  </ArticleElementWrapper>
                </React.Fragment>
              );
            }

            case "youtube_video": {
              const url = `${YOUTUBE_WATCH_URL}${element.youtubeId}`;
              return (
                <React.Fragment key={index}>
                  <ArticleElementWrapper>
                    <YouTubeEmbedCustom url={url} className="my-lg" />
                  </ArticleElementWrapper>
                </React.Fragment>
              );
            }

            case "brightcove_video": {
              return (
                <React.Fragment key={index}>
                  <ArticleElementWrapper>
                    <WrapperContainer className="block my-lg">
                      <BrightcoveVideo videoId={element.videoId} />
                    </WrapperContainer>
                  </ArticleElementWrapper>
                </React.Fragment>
              );
            }

            default:
              return null;
          }
        })}
      {newDesign && (
        <ArticleElementWrapper>
          {isArrayWithElements(attachments) && (
            <div className="my-6 lg:my-8 flex flex-col gap-3">
              {attachments.map((attachment, idx) => (
                <AttachmentCard
                  key={idx}
                  attachment={attachment}
                  newDesign={true}
                />
              ))}
            </div>
          )}

          {isArrayWithElements(relatedArticles) && (
            <div className="my-6 lg:my-8">
              <RelatedArticleNew relatedArticles={relatedArticles} />
            </div>
          )}

          <RenderTags
            tagsWrapper="my-6 lg:my-8 gap-[10px]"
            tags={tags}
            tagClass="bg-grey-1000 px-[10px] py-[3px] not-italic text-[12px] lg:text-[12px] leading-[150%] text-grey-350"
          />
        </ArticleElementWrapper>
      )}
      {isArrayWithElements(relatedArticles) && !newDesign && (
        <ArticleElementWrapper>
          <RelatedArticle relatedArticles={relatedArticles} />
        </ArticleElementWrapper>
      )}
      {showNormalBanner && isCasScriptReady && (
        <ArticleElementWrapper>
          <CASBlock className="mt-md" {...casBlockProps} />
        </ArticleElementWrapper>
      )}
      {translatedArticle && (
        <DraggableCTA
          pageNumber={pageNumber}
          translatedArticle={translatedArticle}
          articleSlotTarget={articleSlotTarget}
          gsChannels={gsChannels}
          isInfiniteScrollArticle={isInfiniteScrollArticle}
          visibleArticleId={visibleArticleId}
          newDesign={newDesign}
        />
      )}
    </div>
  );
}
